// @ts-check
/** @typedef {import('../types/api').Vendor} Vendor */
/** @typedef {import('../types/api').VendorOrder} VendorOrder */
/** @typedef {import('../types/api').VendorAnalytics} VendorAnalytics */
/** @typedef {import('../types/api').QualityIssue} QualityIssue */

import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Edit,
  Mail,
  MapPin,
  Package,
  Phone,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../context/ToastContext';
import vendorService from '../services/vendorService';

const VendorDetailPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  /** @type {[Vendor | null, Function]} */
  const [vendor, setVendor] = useState(null);
  /** @type {[VendorAnalytics | null, Function]} */
  const [analytics, setAnalytics] = useState(null);
  /** @type {[VendorOrder[], Function]} */
  const [orders, setOrders] = useState([]);
  /** @type {[QualityIssue[], Function]} */
  const [qualityIssues, setQualityIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  const fetchVendorDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await vendorService.getVendor(vendorId);
      setVendor(data);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [vendorId, showToast]);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await vendorService.getVendorOrders({ vendor_id: vendorId });
      setOrders(data);
    } catch (error) {
      showToast(error.message, 'error');
    }
  }, [vendorId, showToast]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const data = await vendorService.getVendorAnalytics(vendorId);
      setAnalytics(data);
    } catch (error) {
      showToast(error.message, 'error');
    }
  }, [vendorId, showToast]);

  const fetchQualityIssues = useCallback(async () => {
    try {
      const data = await vendorService.getQualityIssues({ vendor_id: vendorId });
      setQualityIssues(data);
    } catch (error) {
      showToast(error.message, 'error');
    }
  }, [vendorId, showToast]);

  useEffect(() => {
    fetchVendorDetails();
  }, [fetchVendorDetails]);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
    if (activeTab === 'analytics') fetchAnalytics();
    if (activeTab === 'quality') fetchQualityIssues();
  }, [activeTab, fetchOrders, fetchAnalytics, fetchQualityIssues]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await vendorService.deleteVendor(vendorId);
      showToast('Vendor deleted successfully', 'success');
      navigate('/vendors');
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'text-muted-foreground/70';
    if (rating >= 4) return 'text-green-600 dark:text-green-400';
    if (rating >= 3) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-muted text-foreground',
      sent: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
      confirmed: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-300',
      in_production: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
      ready_for_pickup: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
      delivered: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      completed: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!vendor) {
    return (
      <div>
        <p className="text-center text-muted-foreground">Vendor not found</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/vendors')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft size={20} />
          Back to Vendors
        </button>

        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-foreground">{vendor.vendor_name}</h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  vendor.status === 'active'
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                    : 'bg-muted text-foreground'
                }`}
              >
                {vendor.status}
              </span>
              <span className="bg-muted text-foreground px-3 py-1 rounded-lg font-mono text-sm font-semibold">
                {vendor.vendor_initials}
              </span>
            </div>
            <p className="text-muted-foreground">{vendor.company_name}</p>
            <p className="text-sm text-muted-foreground mt-1">{vendor.vendor_type.replace('_', ' ')}</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/vendors/edit/${vendorId}`)}
              className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
            >
              <Edit size={18} />
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/70 transition"
            >
              <Trash2 size={18} />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card rounded-lg shadow overflow-hidden">
        <div className="flex border-b overflow-x-auto">
          {[
            { key: 'info', label: 'Information', icon: Building2 },
            { key: 'orders', label: 'Orders', icon: Package },
            { key: 'analytics', label: 'Analytics', icon: TrendingUp },
            { key: 'quality', label: 'Quality Issues', icon: AlertCircle },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 whitespace-nowrap flex items-center gap-2 px-6 py-4 text-sm font-medium transition ${
                activeTab === tab.key
                  ? 'border-b-2 border-foreground text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {vendor.contact_person && (
                    <div className="flex items-center gap-3">
                      <Building2 className="text-muted-foreground/70" size={20} />
                      <div>
                        <p className="text-sm text-muted-foreground">Contact Person</p>
                        <p className="text-foreground">{vendor.contact_person}</p>
                      </div>
                    </div>
                  )}
                  {vendor.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="text-muted-foreground/70" size={20} />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="text-foreground">{vendor.email}</p>
                      </div>
                    </div>
                  )}
                  {vendor.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="text-muted-foreground/70" size={20} />
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <p className="text-foreground">{vendor.phone}</p>
                      </div>
                    </div>
                  )}
                  {vendor.address && (
                    <div className="flex items-start gap-3 md:col-span-2">
                      <MapPin className="text-muted-foreground/70 flex-shrink-0" size={20} />
                      <div>
                        <p className="text-sm text-muted-foreground">Address</p>
                        <p className="text-foreground">{vendor.address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tax & Banking */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Tax & Banking Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {vendor.gst_number && (
                    <div>
                      <p className="text-sm text-muted-foreground">GST Number</p>
                      <p className="text-foreground font-mono">{vendor.gst_number}</p>
                    </div>
                  )}
                  {vendor.pan_number && (
                    <div>
                      <p className="text-sm text-muted-foreground">PAN Number</p>
                      <p className="text-foreground font-mono">{vendor.pan_number}</p>
                    </div>
                  )}
                  {vendor.preferred_payment_method && (
                    <div>
                      <p className="text-sm text-muted-foreground">Preferred Payment Method</p>
                      <p className="text-foreground">{vendor.preferred_payment_method}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Ratings */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Performance Ratings</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground mb-2">Quality</p>
                    <p className={`text-3xl font-bold ${getRatingColor(vendor.quality_rating)}`}>
                      {vendor.quality_rating ? parseFloat(String(vendor.quality_rating)).toFixed(1) : 'N/A'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">out of 5.0</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground mb-2">Delivery</p>
                    <p className={`text-3xl font-bold ${getRatingColor(vendor.delivery_rating)}`}>
                      {vendor.delivery_rating ? parseFloat(String(vendor.delivery_rating)).toFixed(1) : 'N/A'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">out of 5.0</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground mb-2">Response</p>
                    <p className={`text-3xl font-bold ${getRatingColor(vendor.response_rating)}`}>
                      {vendor.response_rating ? parseFloat(String(vendor.response_rating)).toFixed(1) : 'N/A'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">out of 5.0</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {vendor.notes && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Notes</h3>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-foreground/80 whitespace-pre-wrap">{vendor.notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-foreground">Vendor Orders</h3>
                <button
                  onClick={() => navigate('/vendors/orders/create')}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition text-sm"
                >
                  Create Order
                </button>
              </div>

              {orders.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No orders found for this vendor</p>
              ) : (
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      onClick={() => navigate(`/vendors/orders/${order.id}`)}
                      className="border border-border rounded-lg p-4 hover:shadow-md transition cursor-pointer"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-mono font-semibold text-foreground">{order.order_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(order.order_date).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="text-sm text-muted-foreground">
                          {order.expected_delivery_date &&
                            `Due: ${new Date(order.expected_delivery_date).toLocaleDateString()}`}
                        </span>
                        <span className="font-semibold text-foreground">
                          ₹{parseFloat(String(order.total_amount)).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Vendor Analytics</h3>

              {analytics ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 dark:bg-blue-950/50 rounded-lg p-6">
                    <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Total Spend</p>
                    <p className="text-3xl font-bold text-blue-900 dark:text-blue-300">
                      ₹{analytics.total_spend.toLocaleString('en-IN')}
                    </p>
                  </div>

                  <div className="bg-green-50 dark:bg-green-950/50 rounded-lg p-6">
                    <p className="text-sm text-green-600 dark:text-green-400 mb-1">Total Tax Paid</p>
                    <p className="text-3xl font-bold text-green-900 dark:text-green-300">
                      ₹{analytics.total_tax_paid.toLocaleString('en-IN')}
                    </p>
                  </div>

                  <div className="bg-purple-50 dark:bg-purple-950/50 rounded-lg p-6">
                    <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">Total Orders</p>
                    <p className="text-3xl font-bold text-purple-900 dark:text-purple-300">{analytics.order_count}</p>
                  </div>

                  <div className="bg-yellow-50 dark:bg-yellow-950/50 rounded-lg p-6">
                    <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-1">Avg Delivery Time</p>
                    <p className="text-3xl font-bold text-yellow-900 dark:text-yellow-300">
                      {analytics.average_delivery_time_days} days
                    </p>
                  </div>

                  <div className="bg-red-50 dark:bg-red-950/50 rounded-lg p-6">
                    <p className="text-sm text-red-600 dark:text-red-400 mb-1">Quality Issues</p>
                    <p className="text-3xl font-bold text-red-900 dark:text-red-300">{analytics.quality_issue_count}</p>
                  </div>

                  <div className="bg-orange-50 dark:bg-orange-950/50 rounded-lg p-6">
                    <p className="text-sm text-orange-600 dark:text-orange-400 mb-1">Pending Payments</p>
                    <p className="text-3xl font-bold text-orange-900 dark:text-orange-300">
                      ₹{analytics.pending_payments.toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-12">Loading analytics...</p>
              )}
            </div>
          )}

          {/* Quality Issues Tab */}
          {activeTab === 'quality' && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Quality Issues Log</h3>

              {qualityIssues.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No quality issues reported for this vendor</p>
              ) : (
                <div className="space-y-3">
                  {qualityIssues.map((issue) => (
                    <div key={issue.id} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-foreground">{issue.issue_type.replace('_', ' ')}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(issue.issue_date).toLocaleDateString()}
                          </p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            issue.severity === 'critical'
                              ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                              : issue.severity === 'high'
                                ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
                                : issue.severity === 'medium'
                                  ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                                  : 'bg-muted text-foreground'
                          }`}
                        >
                          {issue.severity}
                        </span>
                      </div>
                      <p className="text-foreground/80 mb-2">{issue.issue_description}</p>
                      {Number(issue.impact_on_timeline) > 0 && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          ⏱️ Delayed by {issue.impact_on_timeline} days
                        </p>
                      )}
                      {issue.resolution && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm text-muted-foreground mb-1">Resolution:</p>
                          <p className="text-sm text-foreground/80">{issue.resolution}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorDetailPage;
